import { Component, OnInit, ChangeDetectionStrategy, ChangeDetectorRef, DestroyRef, ViewChild, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { Subject, debounceTime, switchMap } from 'rxjs';
import Swal from '../../../shared/utils/swal.util';
import { AuthService } from 'app/core/services/auth/auth.service';
import { GrupoResultadoBusca, ResultadoBusca } from 'app/shared/utils/busca-global.model';
import { BuscaGlobalService } from 'app/shared/utils/busca-global.service';
import { ConfiguracoesComponent } from '../configuracoes/configuracoes.component';
import { MeuPerfilComponent } from '../meu-perfil/meu-perfil.component';
import { PerfilUsuarioService } from '../meu-perfil/perfil-usuario.service';
import { TrocarClienteService } from '../trocar-cliente/trocar-cliente.service';
import { ThemeSettingsPanelComponent } from '../theme-settings-panel/theme-settings-panel.component';

const ROTA_BUSCA = '/adm/busca';
const ESPERA_DIGITACAO = 250;
const MINIMO_BUSCA = 2;
const VER_TODOS = 'ver-todos';
const NOME_PADRAO = 'Portal Administrativo';

@Component({
  selector: 'app-main-header',
  templateUrl: './main-header.component.html',
  styleUrls: ['./main-header.component.css'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class MainHeaderComponent implements OnInit {
  private readonly destroyRef = inject(DestroyRef);
  private readonly perfilUsuarioService = inject(PerfilUsuarioService);
  private readonly buscaGlobalService = inject(BuscaGlobalService);
  private readonly changeDetectorRef = inject(ChangeDetectorRef);
  private readonly trocarClienteService = inject(TrocarClienteService);
  private readonly termoDigitado = new Subject<string>();

  @ViewChild(MatAutocompleteTrigger) private readonly gatilhoBusca?: MatAutocompleteTrigger;

  readonly verTodos = VER_TODOS;
  readonly exibirTermo = (): string => this.termoBusca;

  nome = '';
  perfil = '';
  nomeSocial = '';
  email = '';
  nomeFantasia = NOME_PADRAO;
  termoBusca = '';
  termoConsultado = '';
  gruposSugestoes: GrupoResultadoBusca[] = [];
  totalSugestoes = 0;
  buscando = false;
  previewImage: string | null = null;
  showProfile = false;

  constructor(
    public readonly dialog: MatDialog,
    private readonly router: Router,
    private readonly authService: AuthService,
  ) { }

  ngOnInit(): void {
    this.acompanharBusca();
    this.acompanharPerfil();
    this.acompanharOrgao();
  }

  get termoAtual(): string {
    return this.termoBusca.trim();
  }

  get buscaAtiva(): boolean {
    return this.termoAtual.length >= MINIMO_BUSCA;
  }

  get exibirCarregando(): boolean {
    return this.buscaAtiva && this.buscando;
  }

  get exibirResultados(): boolean {
    return this.buscaAtiva && !this.buscando && this.totalSugestoes > 0;
  }

  get exibirVazio(): boolean {
    return this.buscaAtiva
      && !this.buscando
      && this.termoConsultado === this.termoAtual
      && this.totalSugestoes === 0;
  }

  private acompanharPerfil(): void {
    this.perfilUsuarioService.perfil$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((perfil) => {
        this.nome = perfil.nome;
        this.nomeSocial = perfil.nomeSocial ?? '';
        this.email = perfil.email;
        this.perfil = perfil.perfil;
        this.previewImage = perfil.midia;
      });
  }

  private acompanharOrgao(): void {
    this.authService.currentUser
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((usuario) => {
        this.nomeFantasia = usuario?.orgao?.trim() || NOME_PADRAO;
        this.changeDetectorRef.markForCheck();
      });
  }

  private acompanharBusca(): void {
    this.termoDigitado
      .pipe(
        debounceTime(ESPERA_DIGITACAO),
        switchMap((termo) => this.buscaGlobalService.sugerir(termo)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((resposta) => {
        this.buscando = false;
        this.termoConsultado = resposta.termo;
        this.totalSugestoes = resposta.total;
        this.gruposSugestoes = this.buscaGlobalService.agrupar(resposta.itens);

        this.changeDetectorRef.markForCheck();
      });
  }

  filtrar(): void {
    if (!this.buscaAtiva) {
      this.zerarSugestoes();
      return;
    }

    this.buscando = true;
    this.termoDigitado.next(this.termoAtual);
  }

  selecionar(valor: ResultadoBusca | string): void {
    if (valor === VER_TODOS) {
      this.verTodosResultados();
      return;
    }

    const resultado = valor as ResultadoBusca;

    this.router.navigateByUrl(this.buscaGlobalService.urlDoResultado(resultado));
  }

  buscarTudo(): void {
    if (this.gatilhoBusca?.activeOption) {
      return;
    }

    this.verTodosResultados();
  }

  verTodosResultados(): void {
    if (!this.buscaAtiva) {
      return;
    }

    this.gatilhoBusca?.closePanel();
    this.router.navigate([ROTA_BUSCA], { queryParams: { termo: this.termoAtual } });
  }

  limpar(): void {
    this.termoBusca = '';
    this.zerarSugestoes();
  }

  private zerarSugestoes(): void {
    this.termoConsultado = '';
    this.gruposSugestoes = [];
    this.totalSugestoes = 0;
    this.buscando = false;
  }

  trackByResultado(_index: number, resultado: ResultadoBusca): string {
    return resultado.id;
  }

  toggleProfile(): void {
    this.showProfile = !this.showProfile;
  }

  logout(): void {
    this.showProfile = false;
    this.authService.logout();
    this.router.navigateByUrl('/authentication/signin');
  }

  getInitials(nome: string | null | undefined): string {
    if (!nome) return '';

    const partes = nome.trim().split(' ').filter((parte) => parte.length > 0);

    if (partes.length === 1) {
      return partes[0].substring(0, 2).toUpperCase();
    }

    return partes
      .slice(0, 2)
      .map((parte) => parte[0].toUpperCase())
      .join('');
  }

  trocarCliente(): void {
    this.trocarClienteService.abrir();
  }

  configuracao(): void {
    this.dialog.open(ConfiguracoesComponent, {
      data: { data: {}, action: 'Configurações' },
      disableClose: true,
      panelClass: 'dialog-rounded',
      maxWidth: 'auto',
      maxHeight: '100vh'
    });
  }

  meuPerfil(): void {
    this.showProfile = false;

    const dialogRef = this.dialog.open(MeuPerfilComponent, {
      data: { data: {}, action: 'Meu Perfil' },
      disableClose: true,
      panelClass: 'dialog-rounded',
      width: '1080px',
      maxWidth: '95vw',
      maxHeight: '100vh'
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result?.success) {
        Swal.fire('Sucesso', result.message || 'Operação realizada com sucesso!', 'success');
      }
    });
  }

  abrirPainelTema(): void {
    this.dialog.open(ThemeSettingsPanelComponent, {
      position: { top: '0', right: '0' },
      width: '380px',
      maxWidth: '100vw',
      height: '100vh',
      autoFocus: false,
      panelClass: 'theme-settings-dialog'
    });
  }
}
